/**
 * FriendlyErrorView
 * Exibe mensagem amigável de erro ao usuário, nunca o erro técnico.
 * Usa as configurações de suporte (support_config) para contato.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Alert,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiService } from '@services/api';
import logger from '@utils/logger';

interface SupportConfig {
  contact: {
    email: string;
    whatsapp: string;
    hours: string;
  };
  messages: {
    errorTitle?: string;
    errorDescription?: string;
    errorContactInstruction?: string;
  };
}

interface FriendlyErrorViewProps {
  onRetry?: () => void;
  /** Título customizado (sobrescreve o da config) */
  customTitle?: string;
  /** Descrição customizada (sobrescreve o da config) */
  customDescription?: string;
}

const DEFAULT_TITLE = 'Algo deu errado';
const DEFAULT_DESCRIPTION = 'Ocorreu um erro inesperado. Por favor, tente novamente.';
const DEFAULT_CONTACT_INSTRUCTION = 'Se o problema persistir, entre em contato com nosso suporte:';

const FriendlyErrorView: React.FC<FriendlyErrorViewProps> = ({
  onRetry,
  customTitle,
  customDescription,
}) => {
  const [supportConfig, setSupportConfig] = useState<SupportConfig | null>(null);

  const loadSupportConfig = useCallback(async () => {
    try {
      const response = await apiService.getSupportConfig();
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setSupportConfig({
          contact: {
            email: data.contact?.email ?? '',
            whatsapp: data.contact?.whatsapp ?? '',
            hours: data.contact?.hours ?? '',
          },
          messages: {
            errorTitle: data.messages?.errorTitle ?? '',
            errorDescription: data.messages?.errorDescription ?? '',
            errorContactInstruction: data.messages?.errorContactInstruction ?? '',
          },
        });
      }
    } catch (err) {
      logger.debug('Could not load support config for error view', err);
    }
  }, []);

  useEffect(() => {
    loadSupportConfig();
  }, [loadSupportConfig]);

  const title = (customTitle || supportConfig?.messages?.errorTitle || DEFAULT_TITLE).trim() || DEFAULT_TITLE;
  const description = customDescription ?? supportConfig?.messages?.errorDescription ?? DEFAULT_DESCRIPTION;
  const contactInstruction = supportConfig?.messages?.errorContactInstruction ?? DEFAULT_CONTACT_INSTRUCTION;
  const contactEmail = supportConfig?.contact?.email ?? '';
  const contactWhatsapp = supportConfig?.contact?.whatsapp ?? '';
  const contactHours = supportConfig?.contact?.hours ?? '';
  const hasContact = contactEmail || contactWhatsapp || contactHours;

  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center',
        }}
      >
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom color="error">
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480 }}>
          {description}
        </Typography>

        {hasContact && (
          <Alert
            severity="info"
            sx={{
              width: '100%',
              maxWidth: 480,
              mb: 3,
              textAlign: 'left',
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
              {contactInstruction}
            </Typography>
            {contactEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmailIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">{contactEmail}</Typography>
              </Box>
            )}
            {contactWhatsapp && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MessageIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">{contactWhatsapp}</Typography>
              </Box>
            )}
            {contactHours && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">{contactHours}</Typography>
              </Box>
            )}
          </Alert>
        )}

        {onRetry && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            sx={{ mt: 2 }}
          >
            Tentar Novamente
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default FriendlyErrorView;
